import type { AssetId, DocumentId, ModelId, RevisionId } from '@/shared/types/common.types';
import type { PersonRef } from '@/features/workspace/types/workspace.types';

export const DOCUMENT_TYPES = ['General', 'Sheet', 'Bep', 'Contract', 'ProjectManifest', 'Other'] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number] | 'Unknown';

export const CLASSIFICATIONS = ['Normal', 'Restricted'] as const;
export type Classification = (typeof CLASSIFICATIONS)[number] | 'Unknown';

/** ISO 19650 container states, shared with Merge Model Requests. */
export const REVISION_STATUSES = ['Wip', 'Shared', 'Published', 'Archive'] as const;
export type RevisionStatus = (typeof REVISION_STATUSES)[number] | 'Unknown';

/** Whether the revision's file has passed verification and can be opened. */
export const AVAILABILITIES = ['Processing', 'Available', 'Failed'] as const;
export type Availability = (typeof AVAILABILITIES)[number] | 'Unknown';

export interface RevisionRef {
  readonly id: RevisionId;
  readonly versionNumber: number;
  readonly revisionCode: string;
  readonly status: RevisionStatus;
  readonly availability: Availability;
}

/** A row in the documents list. */
export interface DocumentSummary {
  readonly id: DocumentId;
  readonly documentType: DocumentType;
  readonly classification: Classification;
  readonly title: string;
  readonly documentNumber: string | null;
  readonly disciplineCode: string | null;
  /** The newest revision the caller may see; `null` only if none is visible yet. */
  readonly currentRevision: RevisionRef | null;
  readonly isDeleted: boolean;
  readonly updatedAt: string;
  readonly updatedBy: PersonRef;
}

export interface DocumentDetail extends DocumentSummary {
  readonly description: string | null;
  readonly sheet: { readonly sheetName: string; readonly modelId: ModelId | null } | null;
  readonly createdAt: string;
  readonly createdBy: PersonRef;
  readonly etag: string;
  /** What the server will allow; hiding actions on these is UX only. */
  readonly permissions: {
    readonly canEdit: boolean;
    readonly canUploadRevision: boolean;
    readonly canChangeStatus: boolean;
    readonly canDelete: boolean;
  };
}

export interface DocumentFacets {
  readonly disciplines: readonly { readonly code: string; readonly count: number }[];
  readonly statuses: readonly { readonly status: RevisionStatus; readonly count: number }[];
}

export type DocumentSort = 'updatedAt' | 'title' | 'number';

export interface DocumentListFilters {
  readonly type: Exclude<DocumentType, 'Unknown'> | null;
  readonly statuses: readonly Exclude<RevisionStatus, 'Unknown'>[];
  readonly disciplines: readonly string[];
  readonly q: string;
  readonly sort: DocumentSort;
  /** Deleted documents instead of live ones; only document controllers and project admins get any. */
  readonly deleted?: boolean;
}

/** `POST documents`: a new document whose first revision is an uploaded file. */
export interface CreateDocumentInput {
  readonly assetId: AssetId;
  readonly documentType: Exclude<DocumentType, 'Unknown'>;
  readonly title: string;
  readonly documentNumber: string | null;
  readonly disciplineCode: string | null;
  readonly description: string | null;
  readonly revisionCode: string;
  readonly classification: Exclude<Classification, 'Unknown'>;
  readonly sheet: { readonly sheetName: string; readonly modelId: number | null } | null;
}

/** One version of a document's file, with its own ISO 19650 status. */
export interface DocumentRevision extends RevisionRef {
  /** Why verification refused the file, when `availability` is `Failed`. */
  readonly failureCode: string | null;
  readonly description: string | null;
  readonly fileName: string;
  readonly sizeBytes: number;
  /** Known once verification has read the file. */
  readonly contentType: string | null;
  readonly sha256: string | null;
  readonly createdAt: string;
  readonly createdBy: PersonRef;
}

/** A short-lived storage URL for a revision's file. */
export interface ContentUrl {
  readonly url: string;
  readonly expiresAt: string;
}

/** `PATCH document`: only the fields that change. */
export interface UpdateDocumentInput {
  readonly title?: string;
  readonly documentNumber?: string | null;
  readonly disciplineCode?: string | null;
  readonly description?: string | null;
  readonly sheet?: { readonly sheetName: string };
}

/** `POST revisions`: a new version of an existing document from an uploaded file. */
export interface NewRevisionInput {
  readonly assetId: AssetId;
  readonly revisionCode: string;
  readonly description: string | null;
}

/** A row in the sheet register: a Sheet document seen by number and name. */
export interface SheetRow {
  readonly documentId: DocumentId;
  readonly sheetNumber: string | null;
  readonly sheetName: string;
  readonly disciplineCode: string | null;
  readonly modelId: ModelId | null;
  readonly currentRevision: RevisionRef | null;
}

export interface SheetListFilters {
  readonly disciplines: readonly string[];
  readonly q: string;
}
